import { useMemo } from 'react';
import { Loader2, DollarSign, TrendingUp, Zap, ArrowUpRight, ArrowDownRight, ArrowUpFromLine, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useEnergyTimeSeries } from '@/hooks/useEnergyTimeSeries';
import { sumCompareDollars } from '@/lib/compareDollars';

/** Whole dollars, or "—" when the figure is unknown (the RPC has no gridCost yet). */
const formatDollars = (value: number | null) =>
  value === null ? '—' : `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
import type { CompareSlot, TimeGranularity, GroupTimeSeriesPoint } from '@/types/reports';
import type { Group, IndustryType } from '@/types';

interface CompareGridRevenueSectionProps {
  slot: CompareSlot;
  dateRange: { start: Date | null; end: Date | null } | null;
  granularity: TimeGranularity;
  groups: Group[];
  selectedIndustryTypes: IndustryType[];
}

export function CompareGridRevenueSection({
  slot,
  dateRange,
  granularity,
  groups: _groups,
  selectedIndustryTypes,
}: CompareGridRevenueSectionProps) {
  // Fetch time series data for this slot
  const { timeSeries: timeSeriesData, isLoading } = useEnergyTimeSeries({
    groupIds: slot.groupIds,
    startDate: dateRange?.start || null,
    endDate: dateRange?.end || null,
    granularity,
    modelCodes: slot.modelCodes,
    industryTypes: selectedIndustryTypes,
    enabled: slot.groupIds.length > 0 && !!dateRange,
  });

  // Calculate grid/revenue data from time series
  const gridData = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return null;
    }

    const totals = timeSeriesData.reduce(
      (acc: { solar: number; grid: number; total: number; gridExport: number }, point: GroupTimeSeriesPoint) => ({
        solar: acc.solar + (point.solarKwh || 0),
        grid: acc.grid + (point.gridKwh || 0),
        total: acc.total + (point.solarKwh || 0) + (point.gridKwh || 0),
        gridExport: acc.gridExport + (point.gridExportKwh || 0),
      }),
      { solar: 0, grid: 0, total: 0, gridExport: 0 }
    );

    // Dollars come from the energy RPC, each unit-day at its configured rate:
    // the same figures as the dashboard and the Excel export (audit F03).
    // Grid cost and total value are null ("—") if the RPC has no gridCost yet.
    const { solarSavings, netSavings, gridCost, gridSellRevenue, totalValue } =
      sumCompareDollars(timeSeriesData);

    // Calculate trend from periods
    const periods = timeSeriesData.length;
    const midpoint = Math.floor(periods / 2);
    const firstHalfGrid = timeSeriesData.slice(0, midpoint).reduce((sum: number, p: GroupTimeSeriesPoint) => sum + (p.gridKwh || 0), 0);
    const secondHalfGrid = timeSeriesData.slice(midpoint).reduce((sum: number, p: GroupTimeSeriesPoint) => sum + (p.gridKwh || 0), 0);
    const gridTrend = firstHalfGrid > 0
      ? ((secondHalfGrid - firstHalfGrid) / firstHalfGrid) * 100
      : 0;

    // Calculate export trend
    const firstHalfExport = timeSeriesData.slice(0, midpoint).reduce((sum: number, p: GroupTimeSeriesPoint) => sum + (p.gridExportKwh || 0), 0);
    const secondHalfExport = timeSeriesData.slice(midpoint).reduce((sum: number, p: GroupTimeSeriesPoint) => sum + (p.gridExportKwh || 0), 0);
    const exportTrend = firstHalfExport > 0
      ? ((secondHalfExport - firstHalfExport) / firstHalfExport) * 100
      : 0;

    return {
      gridKwh: totals.grid,
      solarKwh: totals.solar,
      totalKwh: totals.total,
      gridExportKwh: totals.gridExport,
      gridCost,
      solarSavings,
      netSavings,
      gridSellRevenue,
      totalValue,
      gridPercent: totals.total > 0 ? (totals.grid / totals.total) * 100 : 0,
      gridTrend,
      exportTrend,
      periods,
    };
  }, [timeSeriesData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[180px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!gridData) {
    return (
      <div className="flex items-center justify-center h-full min-h-[180px] text-sm text-muted-foreground">
        No data available for selected period
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Value Card */}
      <div
        className="rounded-lg p-4 text-center relative"
        style={{ backgroundColor: `${slot.color}15` }}
      >
        {/* Info tooltip - top right */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors">
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs bg-popover text-popover-foreground border border-border shadow-lg">
              <div className="text-xs space-y-1">
                <div className="font-medium mb-1.5">Total Value Calculation:</div>
                <div className="flex justify-between gap-4">
                  <span className="text-[#8FB560]">Solar Savings:</span>
                  <span className="text-[#8FB560]">+${gridData.solarSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-amber-500">Grid Revenue:</span>
                  <span className="text-amber-500">+${gridData.gridSellRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-red-400">Grid Cost:</span>
                  <span className="text-red-400">{gridData.gridCost === null ? '—' : `-${formatDollars(gridData.gridCost)}`}</span>
                </div>
                <div className="border-t border-border pt-1 mt-1 flex justify-between gap-4 font-medium">
                  <span>Total Value:</span>
                  <span>{formatDollars(gridData.totalValue)}</span>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="flex items-center justify-center gap-2 mb-2">
          <DollarSign className="h-6 w-6" style={{ color: slot.color }} />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Value</span>
        </div>
        <div className="text-3xl font-bold" style={{ color: slot.color }}>
          {formatDollars(gridData.totalValue)}
        </div>
      </div>

      {/* Grid Usage Details */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-4 w-4 text-yellow-500" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Grid Used</span>
          </div>
          <div className="text-base font-semibold text-right">
            {gridData.gridKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="text-xs font-normal text-muted-foreground ml-1">kWh</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1 text-right">
            {formatDollars(gridData.gridCost)} cost
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <ArrowUpFromLine className="h-4 w-4 text-[#8FB560]" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Grid Export</span>
          </div>
          <div className="text-base font-semibold text-right">
            {gridData.gridExportKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="text-xs font-normal text-muted-foreground ml-1">kWh</span>
          </div>
          <div className="text-xs text-amber-500 mt-1 text-right">
            ${gridData.gridSellRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })} revenue
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-yellow-500" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Grid Trend</span>
          </div>
          <div className={`text-base font-semibold flex items-center justify-end gap-1 ${
            gridData.gridTrend < -5 ? 'text-green-500' : gridData.gridTrend > 5 ? 'text-red-500' : 'text-muted-foreground'
          }`}>
            {gridData.gridTrend < 0 ? (
              <ArrowDownRight className="h-4 w-4" />
            ) : gridData.gridTrend > 0 ? (
              <ArrowUpRight className="h-4 w-4" />
            ) : null}
            {Math.abs(gridData.gridTrend).toFixed(1)}%
          </div>
          <div className="text-xs text-muted-foreground mt-1 text-right">
            {gridData.gridTrend < -5 ? 'Less grid usage' : gridData.gridTrend > 5 ? 'More grid usage' : 'Stable usage'}
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-[#8FB560]" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Export Trend</span>
          </div>
          <div className={`text-base font-semibold flex items-center justify-end gap-1 ${
            gridData.exportTrend > 5 ? 'text-green-500' : gridData.exportTrend < -5 ? 'text-amber-500' : 'text-muted-foreground'
          }`}>
            {gridData.exportTrend > 0 ? (
              <ArrowUpRight className="h-4 w-4" />
            ) : gridData.exportTrend < 0 ? (
              <ArrowDownRight className="h-4 w-4" />
            ) : null}
            {Math.abs(gridData.exportTrend).toFixed(1)}%
          </div>
          <div className="text-xs text-muted-foreground mt-1 text-right">
            {gridData.exportTrend > 5 ? 'More exports' : gridData.exportTrend < -5 ? 'Fewer exports' : 'Stable exports'}
          </div>
        </div>
      </div>

      {/* Additional breakdown */}
      <div className="space-y-2 text-xs">
        <div className="flex justify-between p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Solar Self-Consumed</span>
          <span className="font-medium">{gridData.solarKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
        </div>
        <div className="flex justify-between p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Solar Savings Value</span>
          <span className="font-medium">${gridData.solarSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
        </div>
        <div className="flex justify-between p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Total Energy Consumed</span>
          <span className="font-medium">{gridData.totalKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
        </div>
        <div className="flex justify-between p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Grid Export</span>
          <span className="font-medium">{gridData.gridExportKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
        </div>
        <div className="flex justify-between p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Grid Sell</span>
          <span className="font-medium text-amber-500">${gridData.gridSellRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
        </div>
      </div>

      {/* Grid Percentage Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground uppercase tracking-wider">Grid Dependency</span>
          <span className="font-medium">{gridData.gridPercent.toFixed(1)}%</span>
        </div>
        <div className="h-3 bg-muted/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-yellow-500 rounded-full transition-all duration-300"
            style={{ width: `${gridData.gridPercent}%` }}
          />
        </div>
        <div className="text-xs text-muted-foreground text-center">
          Lower is better • {gridData.periods} {granularity.slice(0, -2)} periods
        </div>
      </div>
    </div>
  );
}
